/**
 * Client-side CSV parser for preview and validation.
 * Handles all IEWRS CSV format variations:
 * - 4 date formats: "Jan 1, 2014" / "1-Jan-15" / "1/1/2014" / "1/1/2014 0:00"
 * - 2 orientations: wide (normal) / transposed (Flu_Incidence_new)
 * - 2 column naming: numbered ("1_Bungwe") / plain ("Bungwe")
 * - BOM markers
 */

// Metric registry — maps CSV filenames to metric types
const METRIC_REGISTRY = {
  'Malaria_Incidences_Data': { metricType: 'malaria_incidence', unit: 'per_1000', category: 'disease' },
  'Flu_Incidence_new': { metricType: 'flu_incidence', unit: 'per_1000', category: 'disease' },
  'Flu_incidence_revised': { metricType: 'flu_incidence_revised', unit: 'per_1000', category: 'disease' },
  'Pneumonia_incidence': { metricType: 'pneumonia_incidence', unit: 'per_1000', category: 'disease' },
  'Diarhoea_incidence': { metricType: 'diarrhea_cases', unit: 'cases', category: 'disease' },
  'Tmax': { metricType: 'temperature_max', unit: 'celsius', category: 'meteorological' },
  'Tmin': { metricType: 'temperature_min', unit: 'celsius', category: 'meteorological' },
  'LST_Day': { metricType: 'lst_day', unit: 'celsius', category: 'meteorological' },
  'LST_Night': { metricType: 'lst_night', unit: 'celsius', category: 'meteorological' },
  'MRT': { metricType: 'mean_radiant_temp', unit: 'celsius', category: 'meteorological' },
  'UTCI': { metricType: 'utci', unit: 'celsius', category: 'meteorological' },
  'RAINFALL': { metricType: 'rainfall', unit: 'mm', category: 'meteorological' },
  'RH': { metricType: 'relative_humidity', unit: 'percent', category: 'meteorological' },
  'SH': { metricType: 'specific_humidity', unit: 'kg_per_kg', category: 'meteorological' },
  'U': { metricType: 'zonal_wind', unit: 'm_per_s', category: 'meteorological' },
  'V': { metricType: 'meridional_wind', unit: 'm_per_s', category: 'meteorological' },
  'W': { metricType: 'vertical_velocity', unit: 'Pa_per_s', category: 'meteorological' },
  'CAPE': { metricType: 'cape', unit: 'J_per_kg', category: 'meteorological' },
  'SLHF': { metricType: 'surface_latent_heat_flux', unit: 'W_per_m2', category: 'meteorological' },
  'SSHF': { metricType: 'surface_sensible_heat_flux', unit: 'W_per_m2', category: 'meteorological' },
  'STRD': { metricType: 'surface_thermal_radiation', unit: 'W_per_m2', category: 'meteorological' },
  'SSRD': { metricType: 'surface_solar_radiation', unit: 'W_per_m2', category: 'meteorological' },
  'SP': { metricType: 'surface_pressure', unit: 'Pa', category: 'meteorological' },
  'SPEI': { metricType: 'spei', unit: 'index', category: 'meteorological' },
  'PM25_all_years_combined_from_2019': { metricType: 'pm25', unit: 'ug_per_m3', category: 'air_pollutant' },
  'CO': { metricType: 'carbon_monoxide', unit: 'mol_per_m2', category: 'air_pollutant' },
  'NO2': { metricType: 'nitrogen_dioxide', unit: 'mol_per_m2', category: 'air_pollutant' },
  'O3': { metricType: 'ozone', unit: 'mol_per_m2', category: 'air_pollutant' },
  'SO2': { metricType: 'sulfur_dioxide', unit: 'mol_per_m2', category: 'air_pollutant' },
  'HCHO': { metricType: 'formaldehyde', unit: 'mol_per_m2', category: 'air_pollutant' },
  'NDWI': { metricType: 'ndwi', unit: 'index', category: 'vegetation_water' },
  'NDVI': { metricType: 'ndvi', unit: 'index', category: 'vegetation_water' },
  'CI': { metricType: 'chlorophyll_index', unit: 'index', category: 'vegetation_water' },
  'EVI': { metricType: 'evi', unit: 'index', category: 'vegetation_water' },
  'RECI': { metricType: 'reci', unit: 'index', category: 'vegetation_water' },
}

const UNIT_LABELS = {
  per_1000: 'per 1,000 population',
  cases: 'cases',
  celsius: '°C',
  mm: 'mm',
  percent: '%',
  index: 'index',
  ug_per_m3: 'µg/m³',
  mol_per_m2: 'mol/m²',
  kg_per_kg: 'kg/kg',
  m_per_s: 'm/s',
  Pa_per_s: 'Pa/s',
  J_per_kg: 'J/kg',
  W_per_m2: 'W/m²',
  Pa: 'Pa',
}

const CATEGORY_LABELS = {
  disease: 'Health / Disease',
  meteorological: 'Meteorological',
  air_pollutant: 'Air Pollutant',
  vegetation_water: 'Vegetation & Water',
}

// Live alias index, hydrated once by contexts/MetricRegistryContext after a
// successful GET /api/data/registry/metrics (metric_type -> { metricType,
// unit, category }, keyed by every csv_aliases filename stem the registry
// knows for that metric — see data/metricCatalog.js's buildCsvAliasIndex).
//
// detectMetricFromFilename below merges this OVER the static METRIC_REGISTRY
// rather than replacing it: registry values win for keys it defines (they
// should be identical for every metric already in METRIC_REGISTRY — the
// registry was seeded by evaluating this exact file, see
// IMPORT_SPEC.md §1.1), and any filename the registry has an alias for but
// this static table doesn't gets picked up too. If the API is unreachable,
// still loading, or this module is used standalone, liveAliasIndex stays
// null and detectMetricFromFilename runs exactly as it always has.
//
// parseCSVPreview/parseCSVFull remain fully synchronous — this only swaps
// which lookup table detectMetricFromFilename reads at call time, it never
// awaits anything mid-parse (this runs client-side, before any import API
// call, per IMPORT_SPEC.md §2.3).
let liveAliasIndex = null

export function setCsvAliasSource(index) {
  liveAliasIndex = index && Object.keys(index).length > 0 ? index : null
}

/**
 * Parse a single CSV line handling quoted fields
 */
function parseCSVLine(line) {
  const result = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  result.push(current.trim())
  return result
}

/**
 * Detect date format from a sample date string
 */
function detectDateFormat(dateStr) {
  if (!dateStr) return 'unknown'
  const s = dateStr.replace(/^["']|["']$/g, '').trim()

  // "Jan 1, 2014" or "Jan 01, 2014"
  if (/^[A-Z][a-z]{2}\s+\d{1,2},?\s+\d{4}$/.test(s)) return 'MMM D, YYYY'
  // "1-Jan-15" or "1-Jan-2015"
  if (/^\d{1,2}-[A-Z][a-z]{2}-\d{2,4}$/.test(s)) return 'D-MMM-YY'
  // "1/1/2014 0:00" or "1/1/2014"
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(s)) return 'M/D/YYYY'
  // "Jan-15" (transposed flu format, month-year only)
  if (/^[A-Z][a-z]{2}-\d{2}$/.test(s)) return 'MMM-YY'

  return 'unknown'
}

/**
 * Parse a date string to a Date object
 */
function parseDate(dateStr) {
  if (!dateStr) return null
  const s = dateStr.replace(/^["']|["']$/g, '').trim()
  const months = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 }

  // "Jan 1, 2014" or "Jan 01, 2014"
  const m1 = s.match(/^([A-Z][a-z]{2})\s+(\d{1,2}),?\s+(\d{4})$/)
  if (m1) return new Date(parseInt(m1[3]), months[m1[1].toLowerCase()], parseInt(m1[2]))

  // "1-Jan-15" or "1-Jan-2015"
  const m2 = s.match(/^(\d{1,2})-([A-Z][a-z]{2})-(\d{2,4})$/)
  if (m2) {
    let year = parseInt(m2[3])
    if (year < 100) year += 2000
    return new Date(year, months[m2[2].toLowerCase()], parseInt(m2[1]))
  }

  // "1/1/2014 0:00" or "1/1/2014"
  const m3 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (m3) return new Date(parseInt(m3[3]), parseInt(m3[1]) - 1, parseInt(m3[2]))

  // "Jan-15" (month-year)
  const m4 = s.match(/^([A-Z][a-z]{2})-(\d{2})$/)
  if (m4) {
    let year = parseInt(m4[2]) + 2000
    return new Date(year, months[m4[1].toLowerCase()], 1)
  }

  return null
}

/**
 * Detect metric type from filename
 */
export function detectMetricFromFilename(filename) {
  // Remove extension and path
  const baseName = filename.replace(/\.[^/.]+$/, '').split('/').pop()

  // Merge order: static table first, live registry values on top — see the
  // liveAliasIndex comment above for why this is a superset, not a swap.
  const registry = liveAliasIndex ? { ...METRIC_REGISTRY, ...liveAliasIndex } : METRIC_REGISTRY

  // Direct match
  if (registry[baseName]) {
    return { ...registry[baseName], matched: true, matchedKey: baseName }
  }

  // Fuzzy match — try partial matches
  for (const [key, value] of Object.entries(registry)) {
    if (baseName.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(baseName.toLowerCase())) {
      return { ...value, matched: true, matchedKey: key }
    }
  }

  return { metricType: baseName.toLowerCase().replace(/\s+/g, '_'), unit: 'unknown', category: 'unknown', matched: false }
}

/**
 * Get all available metrics for dropdown
 */
export function getAvailableMetrics() {
  return Object.entries(METRIC_REGISTRY).map(([key, value]) => ({
    key,
    ...value,
    unitLabel: UNIT_LABELS[value.unit] || value.unit,
    categoryLabel: CATEGORY_LABELS[value.category] || value.category,
  }))
}

/**
 * Get unit display label
 */
export function getUnitLabel(unit) {
  return UNIT_LABELS[unit] || unit
}

/**
 * Get category display label
 */
export function getCategoryLabel(category) {
  return CATEGORY_LABELS[category] || category
}

/**
 * Validate a cell value
 */
function validateCellValue(value) {
  if (value === '' || value === null || value === undefined) return { valid: false, issue: 'empty' }
  if (value === '#REF!' || value === '#REF') return { valid: false, issue: 'ref_error' }
  if (value === '#N/A' || value === '#VALUE!' || value === '#DIV/0!') return { valid: false, issue: 'formula_error' }
  const num = Number(value)
  if (isNaN(num)) return { valid: false, issue: 'non_numeric' }
  return { valid: true, value: num }
}

/**
 * Main CSV preview parser — reads a file client-side and returns structured preview data
 */
export async function parseCSVPreview(file, maxPreviewRows = 100) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = () => reject(new Error('Failed to read file'))

    reader.onload = (e) => {
      try {
        let text = e.target.result
        // Strip BOM
        text = text.replace(/^\uFEFF/, '')

        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '')
        if (lines.length < 2) {
          return reject(new Error('CSV file must have at least a header row and one data row'))
        }

        const headers = parseCSVLine(lines[0])
        const firstHeader = headers[0].toLowerCase().replace(/^["']|["']$/g, '').trim()

        // Detect if transposed (Flu_Incidence_new format)
        const isTransposed = firstHeader === 'sector'

        let dataHeaders, dataRows, totalDataRows, sectorCount

        if (isTransposed) {
          // Transposed: sectors as rows, dates as columns
          // headers[0] = "Sector", headers[1..n] = date columns
          const dateHeaders = headers.slice(1)
          const sectorRows = lines.slice(1).map(parseCSVLine)

          // Pivot for preview: show first maxPreviewRows date columns as rows
          dataHeaders = ['Date', ...sectorRows.slice(0, 20).map(r => r[0])] // First 20 sectors
          dataRows = dateHeaders.slice(0, maxPreviewRows).map((dateStr, di) => {
            const row = [dateStr]
            sectorRows.slice(0, 20).forEach(sr => {
              row.push(sr[di + 1] || '')
            })
            return row
          })
          totalDataRows = dateHeaders.length
          sectorCount = sectorRows.length
        } else {
          // Normal wide format: dates as rows, sectors as columns
          dataHeaders = headers
          dataRows = lines.slice(1, maxPreviewRows + 1).map(parseCSVLine)
          totalDataRows = lines.length - 1
          sectorCount = headers.length - 1
        }

        // Detect date format from first data value
        const dateFormat = detectDateFormat(dataRows[0]?.[0])

        // Detect column naming
        const sampleSector = dataHeaders[1] || ''
        const hasNumberedSectors = /^\d+_/.test(sampleSector.replace(/^["']|["']$/g, ''))

        // Run validation on preview rows
        const qualityIssues = {
          refErrors: 0,
          emptyValues: 0,
          nonNumeric: 0,
          formulaErrors: 0,
          dateParseErrors: 0,
          totalValues: 0,
          issuesBySector: {},
          issuesByType: {},
        }

        // Full scan for quality (read all rows, not just preview)
        const allDataRows = lines.slice(1).map(parseCSVLine)
        allDataRows.forEach((row, rowIdx) => {
          // Validate date
          const date = parseDate(row[0])
          if (!date && row[0]?.trim()) {
            qualityIssues.dateParseErrors++
          }

          // Validate each data cell
          for (let colIdx = 1; colIdx < row.length && colIdx < dataHeaders.length; colIdx++) {
            qualityIssues.totalValues++
            const validation = validateCellValue(row[colIdx])
            if (!validation.valid) {
              const sectorName = dataHeaders[colIdx]
              if (!qualityIssues.issuesBySector[sectorName]) {
                qualityIssues.issuesBySector[sectorName] = { count: 0, types: {} }
              }
              qualityIssues.issuesBySector[sectorName].count++
              qualityIssues.issuesBySector[sectorName].types[validation.issue] =
                (qualityIssues.issuesBySector[sectorName].types[validation.issue] || 0) + 1

              if (!qualityIssues.issuesByType[validation.issue]) {
                qualityIssues.issuesByType[validation.issue] = 0
              }
              qualityIssues.issuesByType[validation.issue]++

              switch (validation.issue) {
                case 'ref_error': qualityIssues.refErrors++; break
                case 'empty': qualityIssues.emptyValues++; break
                case 'non_numeric': qualityIssues.nonNumeric++; break
                case 'formula_error': qualityIssues.formulaErrors++; break
              }
            }
          }
        })

        // Detect metric from filename
        const detectedMetric = detectMetricFromFilename(file.name)

        resolve({
          fileName: file.name,
          fileSize: file.size,
          headers: dataHeaders,
          previewRows: dataRows,
          totalRows: totalDataRows,
          totalColumns: dataHeaders.length,
          sectorCount,
          dateFormat,
          hasNumberedSectors,
          isTransposed,
          detectedMetric,
          qualityIssues,
          parsedAt: new Date().toISOString(),
        })
      } catch (err) {
        reject(new Error(`CSV parse error: ${err.message}`))
      }
    }

    reader.readAsText(file)
  })
}

/**
 * Parse all rows for full data access (used by DataBrowserPage for client-side data)
 */
export async function parseCSVFull(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.onload = (e) => {
      try {
        let text = e.target.result.replace(/^\uFEFF/, '')
        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '')
        const rawHeaders = parseCSVLine(lines[0])
        const rawRows    = lines.slice(1).map(parseCSVLine)

        // Transposed format detection: first header is "Sector" (case-
        // insensitive), data cells in column 0 are sector names, columns
        // 1..N are dates. We normalise to the wide format the backend
        // expects: first column timestamp, subsequent columns sector names.
        // Without this, a transposed CSV shipped the string "Jan-15" etc.
        // as sector headers — the resolver then flagged 120 unknowns.
        const firstHeaderNormalized = (rawHeaders[0] || '').toLowerCase().trim()
        const isTransposed = firstHeaderNormalized === 'sector'
          || firstHeaderNormalized === 'sectors'
          || firstHeaderNormalized === 'location'

        if (!isTransposed) {
          return resolve({ headers: rawHeaders, rows: rawRows, totalRows: rawRows.length, isTransposed: false })
        }

        // Rotate: dates (rawHeaders[1..]) become rows; sector names (column 0
        // of each data row) become headers.
        const dateCols   = rawHeaders.slice(1)
        const sectorCols = rawRows.map(r => r[0])
        const newHeaders = ['system:time_start', ...sectorCols]
        const newRows    = dateCols.map((dateStr, di) => {
          const row = [dateStr]
          for (let si = 0; si < sectorCols.length; si++) {
            row.push(rawRows[si][di + 1] ?? '')
          }
          return row
        })
        resolve({ headers: newHeaders, rows: newRows, totalRows: newRows.length, isTransposed: true })
      } catch (err) {
        reject(err)
      }
    }
    reader.readAsText(file)
  })
}

/**
 * Simulate import progress (for demo before backend exists)
 */
export function simulateImport(totalRows, sectorCount, onProgress, onComplete) {
  let processed = 0
  const totalValues = totalRows * sectorCount
  const batchSize = Math.ceil(totalValues / 25) // 25 progress updates
  let failedCount = 0

  const interval = setInterval(() => {
    processed = Math.min(processed + batchSize, totalValues)
    const newFailed = Math.floor(Math.random() * 3)
    failedCount += newFailed

    onProgress({
      processedRows: Math.floor(processed / sectorCount),
      totalRows,
      processedValues: processed,
      totalValues,
      inserted: processed - failedCount,
      failed: failedCount,
      percentage: Math.round((processed / totalValues) * 100),
      elapsedMs: Date.now(),
    })

    if (processed >= totalValues) {
      clearInterval(interval)
      if (onComplete) {
        onComplete({
          totalRows,
          totalValues,
          inserted: totalValues - failedCount,
          failed: failedCount,
          duration: '12.4s',
          jobId: `import_${Date.now()}`,
        })
      }
    }
  }, 400)

  return () => clearInterval(interval) // cancel function
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export { METRIC_REGISTRY, UNIT_LABELS, CATEGORY_LABELS }
